import React, { useState } from 'react';
import { 
  TrendingUp, 
  Wallet, 
  ShieldAlert, 
  Leaf, 
  Users, 
  ShoppingBag, 
  ArrowUpRight, 
  CheckCircle2, 
  Clock, 
  ShieldCheck,
  Sun, 
  Moon, 
  RefreshCw,
  Send,
  Calendar,
  LifeBuoy,
  FileText,
  Scale,
  MessageSquare,
  Tag,
  Shield
} from 'lucide-react';
import { 
  AreaChart, 
  Area, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { MetricCard } from '../components/common/MetricCard';
import { StatusBadge } from '../components/common/StatusBadge';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { NavViewId } from '../components/layout/Sidebar';
import { DatePreset, ROLE_DISPLAY_NAMES, AdminRole } from '../types/admin';
import { AnalyticBreakdownModal, AnalyticMetricDetails } from '../components/dashboard/AnalyticBreakdownModal';

interface DashboardViewProps {
  onNavigate: (view: NavViewId) => void;
}

interface TimeframeData {
  label: string;
  gmv: number;
  escrowHeld: number;
  activeOrders: number;
  co2Savings: number;
  growthRate: string;
  chartData: Array<{ time: string; primary: number; secondary: number }>;
  donutData: Array<{ name: string; value: number; color: string }>;
}

const TIMEFRAME_METRICS: Record<DatePreset, TimeframeData> = {
  today: {
    label: 'Today (Past 24 Hours)',
    gmv: 185000,
    escrowHeld: 95000,
    activeOrders: 4,
    co2Savings: 18.5,
    growthRate: '+14.2%',
    chartData: [
      { time: '08:00', primary: 25000, secondary: 2015 },
      { time: '10:00', primary: 55000, secondary: 4434 },
      { time: '12:00', primary: 95000, secondary: 7659 },
      { time: '14:00', primary: 130000, secondary: 10481 },
      { time: '16:00', primary: 160000, secondary: 12900 },
      { time: 'Now', primary: 185000, secondary: 14915 },
    ],
    donutData: [
      { name: 'In Escrow', value: 2, color: '#0df2a6' },
      { name: 'Dispatched', value: 1, color: '#00c784' },
      { name: 'Delivered (48h)', value: 1, color: '#38bdf8' },
      { name: 'Settled', value: 2, color: '#10b981' },
      { name: 'Disputed', value: 0, color: '#ec4899' },
    ]
  },
  '7d': {
    label: 'Last 7 Days',
    gmv: 920000,
    escrowHeld: 210000,
    activeOrders: 12,
    co2Savings: 115.0,
    growthRate: '+22.5%',
    chartData: [
      { time: 'Thu', primary: 110000, secondary: 8868 },
      { time: 'Fri', primary: 195000, secondary: 15721 },
      { time: 'Sat', primary: 340000, secondary: 27412 },
      { time: 'Sun', primary: 490000, secondary: 39506 },
      { time: 'Mon', primary: 620000, secondary: 49987 },
      { time: 'Tue', primary: 780000, secondary: 62887 },
      { time: 'Today', primary: 920000, secondary: 74175 },
    ],
    donutData: [
      { name: 'In Escrow', value: 5, color: '#0df2a6' },
      { name: 'Dispatched', value: 3, color: '#00c784' },
      { name: 'Delivered (48h)', value: 2, color: '#38bdf8' },
      { name: 'Settled', value: 8, color: '#10b981' },
      { name: 'Disputed', value: 1, color: '#ec4899' },
    ]
  },
  '30d': {
    label: 'Last 30 Days (Standard Billing Cycle)',
    gmv: 3485000,
    escrowHeld: 412500,
    activeOrders: 28,
    co2Savings: 1842.6,
    growthRate: '+32.4%',
    chartData: [
      { time: 'Week 1', primary: 620000, secondary: 49987 },
      { time: 'Week 2', primary: 1280000, secondary: 103200 },
      { time: 'Week 3', primary: 2050000, secondary: 165281 },
      { time: 'Week 4', primary: 2890000, secondary: 233006 },
      { time: 'Week 5 (Live)', primary: 3485000, secondary: 280978 },
    ],
    donutData: [
      { name: 'In Escrow', value: 14, color: '#0df2a6' },
      { name: 'Dispatched', value: 8, color: '#00c784' },
      { name: 'Delivered (48h)', value: 6, color: '#38bdf8' },
      { name: 'Settled', value: 42, color: '#10b981' },
      { name: 'Disputed', value: 2, color: '#ec4899' },
    ]
  },
  '90d': {
    label: 'Last 90 Days (Quarterly Performance)',
    gmv: 7840000,
    escrowHeld: 620000,
    activeOrders: 45,
    co2Savings: 3920.0,
    growthRate: '+48.1%',
    chartData: [
      { time: 'Jul', primary: 1890000, secondary: 152381 },
      { time: 'Aug', primary: 4530000, secondary: 365231 },
      { time: 'Sep (Live)', primary: 7840000, secondary: 632100 },
    ],
    donutData: [
      { name: 'In Escrow', value: 22, color: '#0df2a6' },
      { name: 'Dispatched', value: 14, color: '#00c784' },
      { name: 'Delivered (48h)', value: 10, color: '#38bdf8' },
      { name: 'Settled', value: 118, color: '#10b981' },
      { name: 'Disputed', value: 4, color: '#ec4899' },
    ]
  },
  ytd: {
    label: 'Year to Date (2026 Annual)',
    gmv: 14250000,
    escrowHeld: 840000,
    activeOrders: 64,
    co2Savings: 7125.0,
    growthRate: '+112.4%',
    chartData: [
      { time: 'Jan', primary: 450000, secondary: 36281 },
      { time: 'Feb', primary: 1100000, secondary: 88687 },
      { time: 'Mar', primary: 2100000, secondary: 169312 },
      { time: 'Apr', primary: 3520000, secondary: 283800 },
      { time: 'May', primary: 5200000, secondary: 419250 },
      { time: 'Jun', primary: 7450000, secondary: 600656 },
      { time: 'Jul', primary: 9340000, secondary: 753037 },
      { time: 'Aug', primary: 11980000, secondary: 965887 },
      { time: 'Sep', primary: 14250000, secondary: 1148906 },
    ],
    donutData: [
      { name: 'In Escrow', value: 35, color: '#0df2a6' },
      { name: 'Dispatched', value: 20, color: '#00c784' },
      { name: 'Delivered (48h)', value: 16, color: '#38bdf8' },
      { name: 'Settled', value: 230, color: '#10b981' },
      { name: 'Disputed', value: 9, color: '#ec4899' },
    ]
  },
  custom: {
    label: 'Custom Selected Range',
    gmv: 2150000,
    escrowHeld: 280000,
    activeOrders: 18,
    co2Savings: 1075.0,
    growthRate: '+18.9%',
    chartData: [
      { time: 'Day 1', primary: 320000, secondary: 25800 },
      { time: 'Day 5', primary: 840000, secondary: 67725 },
      { time: 'Day 10', primary: 1450000, secondary: 116906 },
      { time: 'Day 15', primary: 2150000, secondary: 173343 },
    ],
    donutData: [
      { name: 'In Escrow', value: 8, color: '#0df2a6' },
      { name: 'Dispatched', value: 5, color: '#00c784' },
      { name: 'Delivered (48h)', value: 4, color: '#38bdf8' },
      { name: 'Settled', value: 26, color: '#10b981' },
      { name: 'Disputed', value: 1, color: '#ec4899' },
    ]
  }
};

export const DashboardView: React.FC<DashboardViewProps> = ({ onNavigate }) => {
  const { isDark, toggleTheme } = useTheme();
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  
  const userRole: AdminRole = currentUser?.role || 'super_admin';

  // Date filter states
  const [datePreset, setDatePreset] = useState<DatePreset>('30d');
  const [customStart, setCustomStart] = useState('2026-08-18');
  const [customEnd, setCustomEnd] = useState('2026-09-17');

  // Interactive Analytic Breakdown Modal state
  const [selectedMetric, setSelectedMetric] = useState<AnalyticMetricDetails | null>(null);
  const [isBreakdownOpen, setIsBreakdownOpen] = useState(false);

  const openMetricBreakdown = (details: AnalyticMetricDetails) => {
    setSelectedMetric(details);
    setIsBreakdownOpen(true);
  };

  const currentData = TIMEFRAME_METRICS[datePreset];
  // Strict formula: 7.5% platform fee + 7.5% VAT on fee = 8.0625% net platform revenue
  const netPlatformRevenue = Math.round(currentData.gmv * 0.080625);
  const totalDonutOrders = currentData.donutData.reduce((acc, curr) => acc + curr.value, 0);

  const refreshDashboard = () => {
    setLoading(true);
    setTimeout(() => setLoading(false), 500);
  };

  // ==================== ROLE CONFIGURATIONS ====================
  const ROLE_CONFIGS: Record<AdminRole, {
    bannerTag: string;
    bannerBadge: string;
    bannerTitle: string;
    bannerDesc: string;
    primaryChartTitle: string;
    primaryChartSub: string;
    primaryLegendLabel: string;
    secondaryLegendLabel: string;
    donutTitle: string;
    donutSub: string;
    donutCenterLabel: string;
    feedTitle: string;
    feedSub: string;
    feedNavTarget: NavViewId;
    feedNavText: string;
  }> = {
    super_admin: {
      bannerTag: 'ReUseIt Operational Command',
      bannerBadge: 'Executive Super Admin • Full Access',
      bannerTitle: 'Platform Financial & Escrow Overview',
      bannerDesc: 'Real-time monitoring of Gross Merchandise Value, held escrow balances, automated 48-hr releases, and circular economy carbon diversion.',
      primaryChartTitle: `GMV & Platform Revenue Velocity (${datePreset.toUpperCase()})`,
      primaryChartSub: `Visualizing escrow volume & 8.0625% net settlement fees for ${currentData.label}`,
      primaryLegendLabel: 'Gross (GMV)',
      secondaryLegendLabel: 'Net Revenue',
      donutTitle: 'Escrow Order Stages',
      donutSub: 'Active transaction distribution in selected window',
      donutCenterLabel: 'Orders',
      feedTitle: 'Live Platform Operations Feed',
      feedSub: 'Real-time audit log of escrow movements, dispatches, and verification milestones',
      feedNavTarget: 'escrow',
      feedNavText: 'View All Ledgers'
    },
    mediator: {
      bannerTag: 'Trust & Arbitration Desk',
      bannerBadge: 'Dispute Mediator • Tribunal Clearance',
      bannerTitle: 'Dispute Tribunal & Mediation Command',
      bannerDesc: 'Review contested marketplace transactions, examine buyer/seller photo evidence, arbitrate 48-hour inspection disputes, and release escrow funds.',
      primaryChartTitle: `Tribunal Case Intake & Resolution Velocity (${datePreset.toUpperCase()})`,
      primaryChartSub: `Weekly trend of formal disputes filed vs arbitrated resolutions for ${currentData.label}`,
      primaryLegendLabel: 'Cases Filed',
      secondaryLegendLabel: 'Cases Resolved',
      donutTitle: 'Arbitration Case Outcomes',
      donutSub: 'Distribution of tribunal settlement verdicts',
      donutCenterLabel: 'Verdicts',
      feedTitle: 'Dispute Hearing & Evidence Feed',
      feedSub: 'Real-time timeline of customer claims, evidence uploads, and tribunal payouts',
      feedNavTarget: 'disputes',
      feedNavText: 'Open Tribunal'
    },
    content_editor: {
      bannerTag: 'Catalog & Editorial Ops',
      bannerBadge: 'Content Editor • Catalog Clearance',
      bannerTitle: 'Marketplace Catalog & Editorial Command',
      bannerDesc: 'Screen new listings for counterfeit & contraband, maintain category hierarchies, oversee merchant product quality, and draft eco-lifestyle news.',
      primaryChartTitle: `Catalog Submission & Approval Velocity (${datePreset.toUpperCase()})`,
      primaryChartSub: `Merchant product listings received vs approved for ${currentData.label}`,
      primaryLegendLabel: 'New Listings',
      secondaryLegendLabel: 'Approved',
      donutTitle: 'Catalog Category Distribution',
      donutSub: 'Active listings proportion by re-commerce vertical',
      donutCenterLabel: 'Catalog',
      feedTitle: 'Catalog Moderation & CMS Stream',
      feedSub: 'Live feed of merchant listing approvals, AI flags, and news publications',
      feedNavTarget: 'listings',
      feedNavText: 'Moderate Items'
    },
    support_agent: {
      bannerTag: 'Customer Care & Triage',
      bannerBadge: 'Support Agent • Helpdesk Clearance',
      bannerTitle: 'Customer Care & Ticket Triage Command',
      bannerDesc: 'Triage incoming customer tickets, answer escrow status questions, assist users with KYC verification hurdles, and safeguard community trust.',
      primaryChartTitle: `Support Inquiries & Resolution Pace (${datePreset.toUpperCase()})`,
      primaryChartSub: `Inbound buyer and seller inquiries vs closed tickets for ${currentData.label}`,
      primaryLegendLabel: 'Tickets Open',
      secondaryLegendLabel: 'Closed',
      donutTitle: 'Inquiry Topics Breakdown',
      donutSub: 'Classification of user inquiries and assistance requests',
      donutCenterLabel: 'Inquiries',
      feedTitle: 'Support Helpdesk Activity Feed',
      feedSub: 'Real-time customer inquiries, staff responses, and satisfaction ratings',
      feedNavTarget: 'support',
      feedNavText: 'Open Helpdesk'
    }
  };

  const config = ROLE_CONFIGS[userRole] || ROLE_CONFIGS.super_admin;

  // Role-tailored Chart Datasets
  const getRoleChartData = () => {
    switch (userRole) {
      case 'mediator':
        return currentData.chartData.map((d) => ({
          time: d.time,
          primary: Math.max(1, Math.round(d.primary / 180000)),
          secondary: Math.max(1, Math.round(d.secondary / 28000)),
        }));
      case 'content_editor':
        return currentData.chartData.map((d) => ({
          time: d.time,
          primary: Math.max(2, Math.round(d.primary / 65000)),
          secondary: Math.max(2, Math.round(d.primary / 72000)),
        }));
      case 'support_agent':
        return currentData.chartData.map((d) => ({
          time: d.time,
          primary: Math.max(2, Math.round(d.primary / 90000)),
          secondary: Math.max(2, Math.round(d.secondary / 14000)),
        }));
      case 'super_admin':
      default:
        return currentData.chartData;
    }
  };

  // Role-tailored Donut Datasets
  const getRoleDonutData = () => {
    switch (userRole) {
      case 'mediator':
        return [
          { name: 'Vendor Settlement', value: 12, color: '#10b981' },
          { name: 'Buyer Full Refund', value: 8, color: '#38bdf8' },
          { name: '50/50 Payout Split', value: 4, color: '#a855f7' },
          { name: 'Claim Withdrawn', value: 2, color: '#90cbb7' },
        ];
      case 'content_editor':
        return [
          { name: 'Electronics & Tech', value: 48, color: '#0df2a6' },
          { name: 'Fashion & Vintage', value: 32, color: '#38bdf8' },
          { name: 'Home & Furniture', value: 18, color: '#f59e0b' },
          { name: 'Books & Media', value: 12, color: '#10b981' },
        ];
      case 'support_agent':
        return [
          { name: 'Delivery Tracking', value: 24, color: '#38bdf8' },
          { name: 'Escrow Payout Query', value: 18, color: '#0df2a6' },
          { name: 'NIN / KYC Verification', value: 14, color: '#f59e0b' },
          { name: 'Account & Password', value: 8, color: '#a855f7' },
        ];
      case 'super_admin':
      default:
        return currentData.donutData;
    }
  };

  const activeDonutData = getRoleDonutData();
  const activeDonutTotal = activeDonutData.reduce((acc, curr) => acc + curr.value, 0);

  // Role-tailored Recent Activity Feeds
  const getRoleActivities = () => {
    switch (userRole) {
      case 'mediator':
        return [
          { id: 'm1', title: 'Tribunal Hearing Opened: Damaged Screen', desc: 'Order #ORD-9821 (Samsung Monitor) - Buyer submitted unpacking video.', time: '14m ago', status: 'disputed' },
          { id: 'm2', title: '50/50 Settlement Mutual Agreement', desc: 'Order #ORD-9799 (Leather Sofa) - Buyer refunded ₦11,000; Vendor received ₦10,113.', time: '1h ago', status: 'settled' },
          { id: 'm3', title: 'Escrow Frozen for Contested Claim', desc: '₦45,000 locked on Order #ORD-9842 pending vendor shipping receipt.', time: '2h ago', status: 'held' },
          { id: 'm4', title: 'Counter-Evidence Uploaded by Seller', desc: 'Vendor Tunde B. uploaded dispatch photos from courier hub.', time: '3h ago', status: 'pending' },
        ];
      case 'content_editor':
        return [
          { id: 'c1', title: 'Listing Approved: Apple MacBook Air M1', desc: 'Validated serial number and battery health photos. Published to feed.', time: '8m ago', status: 'settled' },
          { id: 'c2', title: 'AI Counterfeit Quarantine: Designer Bag', desc: 'High risk replica score (94%). Quarantined pending proof of purchase.', time: '35m ago', status: 'disputed' },
          { id: 'c3', title: 'New CMS Story Published: Eco Upcycling Guide', desc: 'Live in mobile app explore tab with 2,400 push notifications queued.', time: '2h ago', status: 'held' },
          { id: 'c4', title: 'Category Re-indexed: Vintage Audio', desc: '38 items re-tagged with carbon credit multiplier badge.', time: '4h ago', status: 'pending' },
        ];
      case 'support_agent':
        return [
          { id: 's1', title: 'Ticket #TK-1092 Resolved: "Where is my payout?"', desc: 'Explained 48-hour inspection countdown. User rated assistance 5 stars.', time: '6m ago', status: 'settled' },
          { id: 's2', title: 'Ticket #TK-1089 In Progress: NIN Verification', desc: 'Assisted seller candidate Fatima with blurred slip re-upload.', time: '22m ago', status: 'pending' },
          { id: 's3', title: 'Buyer Inquiry: Change Drop-off Address', desc: 'Connected buyer with Gokada courier dispatch dispatch desk.', time: '1h ago', status: 'dispatched' },
          { id: 's4', title: 'Password Reset Assistance: Emeka Okafor', desc: 'Sent verified self-service password link via Firebase auth.', time: '2h ago', status: 'held' },
        ];
      case 'super_admin':
      default:
        return [
          { id: '1', title: '₦45,000 Escrow Locked', desc: 'Order #ORD-9842 (iPhone 12 Pro) - Buyer: Chioma Eze', time: '4m ago', status: 'held' },
          { id: '2', title: 'Order Dispatched via Gokada', desc: 'Order #ORD-9839 (Vintage Teak Chair) - Seller: Tunde Bakare', time: '18m ago', status: 'dispatched' },
          { id: '3', title: '48-hr Inspection Auto-Settled', desc: '₦28,000 released to Vendor (7.5% + 7.5% VAT deducted)', time: '1h ago', status: 'settled' },
          { id: '4', title: 'Dispute Filed: Item Damaged', desc: 'Order #ORD-9821 (Samsung Monitor) - Transferred to Tribunal', time: '2h ago', status: 'disputed' },
          { id: '5', title: 'KYC Document Uploaded', desc: 'NIN Slip submitted by verified seller candidate Fatima S.', time: '3h ago', status: 'pending' },
        ];
    }
  };

  const recentActivities = getRoleActivities();

  return (
    <div className="space-y-8 animate-fadeIn">
      {/* Top Banner with Quick Actions and Theme Switcher */}
      <div className="relative overflow-hidden rounded-3xl p-6 sm:p-8 border transition-all
        bg-gradient-to-r from-[#152a23] via-[#1a382e] to-[#152a23] dark:from-[#152a23] dark:via-[#1a382e] dark:to-[#152a23] border-[#22493c] dark:border-[#22493c]
        light:from-white light:via-emerald-50/50 light:to-white light:border-[#D5E4DC] light:shadow-sm
      ">
        <div className="relative z-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <span className="text-xs font-bold uppercase tracking-wider text-brand-400">
                {config.bannerTag}
              </span>
              <span className="w-1.5 h-1.5 rounded-full bg-brand-400"></span>
              <span className="text-[11px] font-extrabold px-2.5 py-0.5 rounded-full bg-brand-500/15 text-brand-300 border border-brand-500/25">
                {config.bannerBadge}
              </span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-white dark:text-white light:text-[#0D1C17] tracking-tight">
              {config.bannerTitle}
            </h2>
            <p className="mt-1 text-sm text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] max-w-2xl leading-relaxed">
              {config.bannerDesc}
            </p>
          </div>

          {/* Theme Switch & Quick Actions Button Group */}
          <div className="flex flex-wrap items-center gap-3">
            {/* Direct Dashboard Theme Toggle */}
            <button
              onClick={toggleTheme}
              className="flex items-center gap-2 px-4 py-2.5 rounded-full text-xs font-bold transition-all border
                bg-[#22493c] hover:bg-[#2a5547] text-white border-brand-500/30 hover:border-brand-500
                light:bg-white light:border-[#D5E4DC] light:text-[#0D1C17] light:hover:bg-gray-50 light:shadow-sm
              "
              title="Toggle Light or Dark Mode"
            >
              {isDark ? (
                <>
                  <Sun className="w-4 h-4 text-amber-300" />
                  <span>Light Mode</span>
                </>
              ) : (
                <>
                  <Moon className="w-4 h-4 text-emerald-700" />
                  <span>Dark Mode</span>
                </>
              )}
            </button>

            <button
              onClick={refreshDashboard}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2.5 rounded-full text-xs font-bold transition-all
                bg-[#22493c] hover:bg-[#2a5547] text-white border border-[#2a5547]
                light:bg-emerald-50 light:text-emerald-700 light:border-emerald-200 light:hover:bg-emerald-100
              "
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
              <span>Refresh</span>
            </button>

            {/* Role-Authorized Primary Action Button */}
            {userRole === 'super_admin' && (
              <button
                onClick={() => onNavigate('broadcast')}
                className="flex items-center gap-2 px-4 py-2.5 rounded-full text-xs font-bold bg-brand-500 hover:bg-brand-400 text-forest-void shadow-glow-sm transition-all"
              >
                <Send className="w-3.5 h-3.5" />
                <span>Send Push Alert</span>
              </button>
            )}

            {userRole === 'content_editor' && (
              <button
                onClick={() => onNavigate('broadcast')}
                className="flex items-center gap-2 px-4 py-2.5 rounded-full text-xs font-bold bg-brand-500 hover:bg-brand-400 text-forest-void shadow-glow-sm transition-all"
              >
                <Send className="w-3.5 h-3.5" />
                <span>Send Push Broadcast</span>
              </button>
            )}

            {userRole === 'mediator' && (
              <button
                onClick={() => onNavigate('disputes')}
                className="flex items-center gap-2 px-4 py-2.5 rounded-full text-xs font-bold bg-purple-500 hover:bg-purple-400 text-white shadow-glow-sm transition-all"
              >
                <Scale className="w-3.5 h-3.5" />
                <span>Open Tribunal Queue</span>
              </button>
            )}

            {userRole === 'support_agent' && (
              <button
                onClick={() => onNavigate('support')}
                className="flex items-center gap-2 px-4 py-2.5 rounded-full text-xs font-bold bg-blue-500 hover:bg-blue-400 text-white shadow-glow-sm transition-all"
              >
                <LifeBuoy className="w-3.5 h-3.5" />
                <span>Open Support Desk</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* DYNAMIC DATE FILTER BAR */}
      <div className="p-4 rounded-2xl border transition-colors flex flex-col md:flex-row items-start md:items-center justify-between gap-4
        bg-[#152a23] dark:bg-[#152a23] border-[#22493c] dark:border-[#22493c]
        light:bg-white light:border-[#D5E4DC] light:shadow-sm
      ">
        <div className="flex items-center gap-2">
          <div className="p-2 rounded-xl bg-brand-500/10 text-brand-400 border border-brand-500/20">
            <Calendar className="w-4 h-4" />
          </div>
          <div>
            <span className="text-xs font-bold text-white dark:text-white light:text-[#0D1C17] block">
              Analytics Timeframe:
            </span>
            <span className="text-[11px] text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063]">
              {currentData.label}
            </span>
          </div>
        </div>

        {/* Filter Presets */}
        <div className="flex flex-wrap items-center gap-2">
          {(
            [
              { id: 'today', label: 'Today' },
              { id: '7d', label: '7D' },
              { id: '30d', label: '30D' },
              { id: '90d', label: '90D' },
              { id: 'ytd', label: 'YTD' },
              { id: 'custom', label: 'Custom' },
            ] as const
          ).map((preset) => (
            <button
              key={preset.id}
              onClick={() => setDatePreset(preset.id)}
              className={`px-3.5 py-1.5 rounded-full text-xs font-bold transition-all
                ${datePreset === preset.id 
                  ? 'bg-brand-500 text-forest-void shadow-glow-sm' 
                  : 'bg-[#10231d] text-[#90cbb7] border border-[#22493c] hover:text-white dark:bg-[#10231d] light:bg-[#F4F8F5] light:border-[#D5E4DC] light:text-[#527063] light:hover:text-[#0D1C17]'
                }
              `}
            >
              {preset.label}
            </button>
          ))}

          {/* Custom Date Inputs */}
          {datePreset === 'custom' && (
            <div className="flex items-center gap-2 ml-2 pl-2 border-l border-[#22493c] dark:border-[#22493c] light:border-[#D5E4DC]">
              <input
                type="date"
                value={customStart}
                onChange={(e) => setCustomStart(e.target.value)}
                className="px-2.5 py-1 rounded-xl text-xs outline-none border transition-colors
                  bg-[#10231d] dark:bg-[#10231d] border-[#22493c] dark:border-[#22493c] text-white
                  light:bg-[#F4F8F5] light:border-[#D5E4DC] light:text-[#0D1C17]"
              />
              <span className="text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] text-xs">to</span>
              <input
                type="date"
                value={customEnd}
                onChange={(e) => setCustomEnd(e.target.value)}
                className="px-2.5 py-1 rounded-xl text-xs outline-none border transition-colors
                  bg-[#10231d] dark:bg-[#10231d] border-[#22493c] dark:border-[#22493c] text-white
                  light:bg-[#F4F8F5] light:border-[#D5E4DC] light:text-[#0D1C17]"
              />
            </div>
          )}
        </div>
      </div>

      {/* ========================================================================= */}
      {/* PRIMARY KPI METRIC CARDS (STRICTLY ADAPTED TO ROLE PRIVILEGES) */}
      {/* ========================================================================= */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {userRole === 'super_admin' && (
          <>
            <MetricCard
              title="Gross Merchandise Value (GMV)"
              value={`₦${currentData.gmv.toLocaleString()}`}
              subtitle={`in ${datePreset.toUpperCase()}`}
              trend={{ value: currentData.growthRate, positive: true }}
              icon={TrendingUp}
              glow={true}
              onClick={() => openMetricBreakdown({
                id: 'gmv',
                title: 'Gross Merchandise Value (GMV)',
                value: `₦${currentData.gmv.toLocaleString()}`,
                subtitle: `in ${datePreset.toUpperCase()}`,
                trend: { value: currentData.growthRate, positive: true },
                category: 'financial'
              })}
            />

            <MetricCard
              title="Escrow Funds in Hold"
              value={`₦${currentData.escrowHeld.toLocaleString()}`}
              subtitle={`Locked across ${currentData.activeOrders} active orders`}
              badge="Guaranteed"
              icon={ShieldCheck}
              glow={true}
              onClick={() => openMetricBreakdown({
                id: 'escrow',
                title: 'Escrow Funds in Hold',
                value: `₦${currentData.escrowHeld.toLocaleString()}`,
                subtitle: `Locked across ${currentData.activeOrders} active orders`,
                badge: 'Guaranteed',
                category: 'operational'
              })}
            />

            <MetricCard
              title="Platform Net Revenue (8.0625%)"
              value={`₦${netPlatformRevenue.toLocaleString()}`}
              subtitle="7.5% fee + 7.5% VAT"
              trend={{ value: currentData.growthRate, positive: true }}
              icon={ShoppingBag}
              onClick={() => openMetricBreakdown({
                id: 'revenue',
                title: 'Platform Net Revenue (8.0625%)',
                value: `₦${netPlatformRevenue.toLocaleString()}`,
                subtitle: '7.5% fee + 7.5% VAT',
                trend: { value: currentData.growthRate, positive: true },
                category: 'financial'
              })}
            />

            <MetricCard
              title="Carbon Offset Diverted"
              value={`${currentData.co2Savings.toLocaleString()} kg`}
              subtitle={`Equivalent to ${Math.round(currentData.co2Savings / 22)} trees planted`}
              trend={{ value: `+${Math.round(currentData.co2Savings * 0.1)} kg`, positive: true }}
              icon={Leaf}
              glow={true}
              onClick={() => openMetricBreakdown({
                id: 'carbon',
                title: 'Carbon Offset Diverted',
                value: `${currentData.co2Savings.toLocaleString()} kg`,
                subtitle: `Equivalent to ${Math.round(currentData.co2Savings / 22)} trees planted`,
                trend: { value: `+${Math.round(currentData.co2Savings * 0.1)} kg`, positive: true },
                category: 'sustainability'
              })}
            />
          </>
        )}

        {userRole === 'mediator' && (
          <>
            <MetricCard
              title="Active Tribunal Disputes"
              value="2 Cases"
              subtitle="Currently in formal arbitration"
              badge="High Priority"
              icon={Scale}
              glow={true}
            />

            <MetricCard
              title="Held Escrow Under Dispute"
              value="₦73,000"
              subtitle="Protected under tribunal freeze"
              badge="Protected"
              icon={ShieldCheck}
              glow={true}
            />

            <MetricCard
              title="Avg Case Resolution Time"
              value="18.4 Hours"
              subtitle="From evidence upload to verdict"
              trend={{ value: "-4.2h faster", positive: true }}
              icon={Clock}
            />

            <MetricCard
              title="Tribunal Resolution Rate"
              value="98.2%"
              subtitle="Settled without court escalation"
              trend={{ value: "+1.5% this month", positive: true }}
              icon={CheckCircle2}
              glow={true}
            />
          </>
        )}

        {userRole === 'content_editor' && (
          <>
            <MetricCard
              title="Active Catalog Listings"
              value="1,420 Items"
              subtitle="Live in Nigerian marketplace"
              trend={{ value: "+14.2% weekly", positive: true }}
              icon={ShoppingBag}
              glow={true}
            />

            <MetricCard
              title="Pending Moderation Queue"
              value="18 Items"
              subtitle="Awaiting photo & description review"
              badge="Action Req."
              icon={Clock}
              glow={true}
            />

            <MetricCard
              title="Quarantined / Replica Flags"
              value="3 Items"
              subtitle="AI vision & keyword flags"
              badge="Quarantined"
              icon={ShieldAlert}
            />

            <MetricCard
              title="Published CMS Stories"
              value="24 Articles"
              subtitle="Live in mobile explore tab"
              trend={{ value: "+3 this month", positive: true }}
              icon={FileText}
              glow={true}
            />
          </>
        )}

        {userRole === 'support_agent' && (
          <>
            <MetricCard
              title="Open Support Tickets"
              value="5 Active"
              subtitle="Pending initial agent reply"
              badge="Live Queue"
              icon={LifeBuoy}
              glow={true}
            />

            <MetricCard
              title="Average First Response"
              value="4.2 Minutes"
              subtitle="Helpdesk turnaround SLA"
              trend={{ value: "-1.8m faster", positive: true }}
              icon={Clock}
              glow={true}
            />

            <MetricCard
              title="Customer Satisfaction (CSAT)"
              value="96.8%"
              subtitle="From 320 buyer & seller reviews"
              trend={{ value: "+2.1% positive", positive: true }}
              icon={CheckCircle2}
              glow={true}
            />

            <MetricCard
              title="Inquiries Resolved Today"
              value="42 Closed"
              subtitle="Across dispute & order inquiries"
              trend={{ value: "+8 vs yesterday", positive: true }}
              icon={TrendingUp}
            />
          </>
        )}
      </div>

      {/* ========================================================================= */}
      {/* SECONDARY KPI ROW (ONLY AUTHORIZED ROUTE SHORTCUTS) */}
      {/* ========================================================================= */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
        {userRole === 'super_admin' && (
          <>
            <div 
              onClick={() => onNavigate('users')}
              className="cursor-pointer p-5 rounded-2xl border transition-all
                bg-[#152a23] dark:bg-[#152a23] border-[#22493c] dark:border-[#22493c] hover:border-brand-500/40
                light:bg-white light:border-[#D5E4DC] light:hover:border-emerald-400
              "
            >
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063]">
                  KYC Verifications Pending
                </span>
                <Users className="w-5 h-5 text-amber-400" />
              </div>
              <div className="mt-3 flex items-baseline justify-between">
                <h4 className="text-2xl font-bold text-white dark:text-white light:text-[#0D1C17]">4 Submissions</h4>
                <span className="text-xs font-semibold text-amber-400 flex items-center">Review Queue <ArrowUpRight className="w-3.5 h-3.5 ml-1" /></span>
              </div>
            </div>

            <div 
              onClick={() => onNavigate('disputes')}
              className="cursor-pointer p-5 rounded-2xl border transition-all
                bg-[#152a23] dark:bg-[#152a23] border-[#22493c] dark:border-[#22493c] hover:border-brand-500/40
                light:bg-white light:border-[#D5E4DC] light:hover:border-emerald-400
              "
            >
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063]">
                  Active Disputes
                </span>
                <ShieldAlert className="w-5 h-5 text-purple-400" />
              </div>
              <div className="mt-3 flex items-baseline justify-between">
                <h4 className="text-2xl font-bold text-white dark:text-white light:text-[#0D1C17]">2 In Tribunal</h4>
                <span className="text-xs font-semibold text-purple-400 flex items-center">Mediate Now <ArrowUpRight className="w-3.5 h-3.5 ml-1" /></span>
              </div>
            </div>

            <div 
              onClick={() => onNavigate('deleted_accounts')}
              className="cursor-pointer p-5 rounded-2xl border transition-all
                bg-[#152a23] dark:bg-[#152a23] border-[#22493c] dark:border-[#22493c] hover:border-rose-500/40
                light:bg-white light:border-[#D5E4DC] light:hover:border-rose-400
              "
            >
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063]">
                  Deleted Accounts Log
                </span>
                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-rose-500/20 text-rose-400">Security Audit</span>
              </div>
              <div className="mt-3 flex items-baseline justify-between">
                <h4 className="text-2xl font-bold text-white dark:text-white light:text-[#0D1C17]">3 Retained</h4>
                <span className="text-xs font-semibold text-rose-400 flex items-center">Inspect Ledgers <ArrowUpRight className="w-3.5 h-3.5 ml-1" /></span>
              </div>
            </div>
          </>
        )}

        {userRole === 'mediator' && (
          <>
            <div 
              onClick={() => onNavigate('disputes')}
              className="cursor-pointer p-5 rounded-2xl border transition-all
                bg-[#152a23] dark:bg-[#152a23] border-[#22493c] dark:border-[#22493c] hover:border-purple-500/40
                light:bg-white light:border-[#D5E4DC] light:hover:border-purple-400
              "
            >
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063]">
                  Tribunal Arbitration Cases
                </span>
                <Scale className="w-5 h-5 text-purple-400" />
              </div>
              <div className="mt-3 flex items-baseline justify-between">
                <h4 className="text-2xl font-bold text-white dark:text-white light:text-[#0D1C17]">2 In Hearings</h4>
                <span className="text-xs font-semibold text-purple-400 flex items-center">Open Docket <ArrowUpRight className="w-3.5 h-3.5 ml-1" /></span>
              </div>
            </div>

            <div 
              onClick={() => onNavigate('escrow')}
              className="cursor-pointer p-5 rounded-2xl border transition-all
                bg-[#152a23] dark:bg-[#152a23] border-[#22493c] dark:border-[#22493c] hover:border-brand-500/40
                light:bg-white light:border-[#D5E4DC] light:hover:border-emerald-400
              "
            >
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063]">
                  Contested Escrow Ledger
                </span>
                <Wallet className="w-5 h-5 text-brand-400" />
              </div>
              <div className="mt-3 flex items-baseline justify-between">
                <h4 className="text-2xl font-bold text-white dark:text-white light:text-[#0D1C17]">₦73,000 Frozen</h4>
                <span className="text-xs font-semibold text-brand-400 flex items-center">Inspect Escrow <ArrowUpRight className="w-3.5 h-3.5 ml-1" /></span>
              </div>
            </div>

            <div 
              onClick={() => onNavigate('support')}
              className="cursor-pointer p-5 rounded-2xl border transition-all
                bg-[#152a23] dark:bg-[#152a23] border-[#22493c] dark:border-[#22493c] hover:border-blue-500/40
                light:bg-white light:border-[#D5E4DC] light:hover:border-blue-400
              "
            >
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063]">
                  Dispute Support Tickets
                </span>
                <LifeBuoy className="w-5 h-5 text-blue-400" />
              </div>
              <div className="mt-3 flex items-baseline justify-between">
                <h4 className="text-2xl font-bold text-white dark:text-white light:text-[#0D1C17]">2 Assigned</h4>
                <span className="text-xs font-semibold text-blue-400 flex items-center">Reply Tickets <ArrowUpRight className="w-3.5 h-3.5 ml-1" /></span>
              </div>
            </div>
          </>
        )}

        {userRole === 'content_editor' && (
          <>
            <div 
              onClick={() => onNavigate('listings')}
              className="cursor-pointer p-5 rounded-2xl border transition-all
                bg-[#152a23] dark:bg-[#152a23] border-[#22493c] dark:border-[#22493c] hover:border-cyan-500/40
                light:bg-white light:border-[#D5E4DC] light:hover:border-cyan-400
              "
            >
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063]">
                  Listing Moderation Queue
                </span>
                <ShoppingBag className="w-5 h-5 text-cyan-400" />
              </div>
              <div className="mt-3 flex items-baseline justify-between">
                <h4 className="text-2xl font-bold text-white dark:text-white light:text-[#0D1C17]">18 Pending</h4>
                <span className="text-xs font-semibold text-cyan-400 flex items-center">Moderate Now <ArrowUpRight className="w-3.5 h-3.5 ml-1" /></span>
              </div>
            </div>

            <div 
              onClick={() => onNavigate('cms')}
              className="cursor-pointer p-5 rounded-2xl border transition-all
                bg-[#152a23] dark:bg-[#152a23] border-[#22493c] dark:border-[#22493c] hover:border-brand-500/40
                light:bg-white light:border-[#D5E4DC] light:hover:border-emerald-400
              "
            >
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063]">
                  CMS Eco-News & Blog
                </span>
                <FileText className="w-5 h-5 text-brand-400" />
              </div>
              <div className="mt-3 flex items-baseline justify-between">
                <h4 className="text-2xl font-bold text-white dark:text-white light:text-[#0D1C17]">24 Published</h4>
                <span className="text-xs font-semibold text-brand-400 flex items-center">Draft Story <ArrowUpRight className="w-3.5 h-3.5 ml-1" /></span>
              </div>
            </div>

            <div 
              onClick={() => onNavigate('broadcast')}
              className="cursor-pointer p-5 rounded-2xl border transition-all
                bg-[#152a23] dark:bg-[#152a23] border-[#22493c] dark:border-[#22493c] hover:border-amber-500/40
                light:bg-white light:border-[#D5E4DC] light:hover:border-amber-400
              "
            >
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063]">
                  Broadcast Push Alerts
                </span>
                <Send className="w-5 h-5 text-amber-400" />
              </div>
              <div className="mt-3 flex items-baseline justify-between">
                <h4 className="text-2xl font-bold text-white dark:text-white light:text-[#0D1C17]">Campaigns</h4>
                <span className="text-xs font-semibold text-amber-400 flex items-center">Create Push <ArrowUpRight className="w-3.5 h-3.5 ml-1" /></span>
              </div>
            </div>
          </>
        )}

        {userRole === 'support_agent' && (
          <>
            <div 
              onClick={() => onNavigate('support')}
              className="cursor-pointer p-5 rounded-2xl border transition-all
                bg-[#152a23] dark:bg-[#152a23] border-[#22493c] dark:border-[#22493c] hover:border-blue-500/40
                light:bg-white light:border-[#D5E4DC] light:hover:border-blue-400
              "
            >
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063]">
                  Support Ticket Helpdesk
                </span>
                <LifeBuoy className="w-5 h-5 text-blue-400" />
              </div>
              <div className="mt-3 flex items-baseline justify-between">
                <h4 className="text-2xl font-bold text-white dark:text-white light:text-[#0D1C17]">5 Open</h4>
                <span className="text-xs font-semibold text-blue-400 flex items-center">Open Desk <ArrowUpRight className="w-3.5 h-3.5 ml-1" /></span>
              </div>
            </div>

            <div 
              onClick={() => onNavigate('users')}
              className="cursor-pointer p-5 rounded-2xl border transition-all
                bg-[#152a23] dark:bg-[#152a23] border-[#22493c] dark:border-[#22493c] hover:border-brand-500/40
                light:bg-white light:border-[#D5E4DC] light:hover:border-emerald-400
              "
            >
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063]">
                  User Directory Lookup
                </span>
                <Users className="w-5 h-5 text-brand-400" />
              </div>
              <div className="mt-3 flex items-baseline justify-between">
                <h4 className="text-2xl font-bold text-white dark:text-white light:text-[#0D1C17]">User Profiles</h4>
                <span className="text-xs font-semibold text-brand-400 flex items-center">Find Users <ArrowUpRight className="w-3.5 h-3.5 ml-1" /></span>
              </div>
            </div>

            <div 
              onClick={() => onNavigate('profile_settings')}
              className="cursor-pointer p-5 rounded-2xl border transition-all
                bg-[#152a23] dark:bg-[#152a23] border-[#22493c] dark:border-[#22493c] hover:border-emerald-500/40
                light:bg-white light:border-[#D5E4DC] light:hover:border-emerald-400
              "
            >
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063]">
                  Staff Security & 2FA
                </span>
                <ShieldCheck className="w-5 h-5 text-emerald-400" />
              </div>
              <div className="mt-3 flex items-baseline justify-between">
                <h4 className="text-2xl font-bold text-white dark:text-white light:text-[#0D1C17]">Protected</h4>
                <span className="text-xs font-semibold text-emerald-400 flex items-center">My Settings <ArrowUpRight className="w-3.5 h-3.5 ml-1" /></span>
              </div>
            </div>
          </>
        )}
      </div>

      {/* ========================================================================= */}
      {/* VISUAL CHARTS GRID (ROLE ADAPTIVE) */}
      {/* ========================================================================= */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Primary Trend Area Chart */}
        <div className="lg:col-span-2 rounded-3xl p-6 sm:p-8 border transition-colors
          bg-[#152a23] dark:bg-[#152a23] border-[#22493c] dark:border-[#22493c]
          light:bg-white light:border-[#D5E4DC] light:shadow-sm
        ">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div>
              <h3 className="text-lg font-bold text-white dark:text-white light:text-[#0D1C17]">
                {config.primaryChartTitle}
              </h3>
              <p className="text-xs text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063]">
                {config.primaryChartSub}
              </p>
            </div>
            <div className="flex items-center gap-4 text-xs font-semibold">
              <div className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded-full bg-brand-500"></span>
                <span className="text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063]">{config.primaryLegendLabel}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded-full bg-cyan-400"></span>
                <span className="text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063]">{config.secondaryLegendLabel}</span>
              </div>
            </div>
          </div>

          <div className="h-72 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={getRoleChartData()} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="primaryGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#0df2a6" stopOpacity={0.4}/>
                    <stop offset="95%" stopColor="#0df2a6" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="secondaryGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#38bdf8" stopOpacity={0.4}/>
                    <stop offset="95%" stopColor="#38bdf8" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#22493c' : '#EBF2EE'} />
                <XAxis dataKey="time" stroke={isDark ? '#90cbb7' : '#527063'} fontSize={12} tickLine={false} />
                <YAxis 
                  stroke={isDark ? '#90cbb7' : '#527063'} 
                  fontSize={12} 
                  tickFormatter={(v) => userRole === 'super_admin' ? `₦${(v / 1000)}k` : `${v}`} 
                  tickLine={false} 
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: isDark ? '#152a23' : '#FFFFFF', 
                    borderColor: isDark ? '#22493c' : '#D5E4DC', 
                    borderRadius: '16px',
                    color: isDark ? '#ffffff' : '#0D1C17',
                    boxShadow: '0 10px 25px rgba(0,0,0,0.3)'
                  }} 
                  formatter={(val: any) => [userRole === 'super_admin' ? `₦${Number(val).toLocaleString()}` : `${val}`, '']}
                />
                <Area type="monotone" dataKey="primary" stroke="#0df2a6" strokeWidth={3} fillOpacity={1} fill="url(#primaryGradient)" />
                <Area type="monotone" dataKey="secondary" stroke="#38bdf8" strokeWidth={2} fillOpacity={1} fill="url(#secondaryGradient)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Role-Specific Donut Distribution */}
        <div className="rounded-3xl p-6 sm:p-8 border transition-colors flex flex-col justify-between
          bg-[#152a23] dark:bg-[#152a23] border-[#22493c] dark:border-[#22493c]
          light:bg-white light:border-[#D5E4DC] light:shadow-sm
        ">
          <div>
            <h3 className="text-lg font-bold text-white dark:text-white light:text-[#0D1C17]">
              {config.donutTitle}
            </h3>
            <p className="text-xs text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063]">
              {config.donutSub}
            </p>
          </div>

          <div className="h-48 my-2 relative">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={activeDonutData}
                  cx="50%"
                  cy="50%"
                  innerRadius={55}
                  outerRadius={80}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {activeDonutData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <span className="text-2xl font-bold text-white dark:text-white light:text-[#0D1C17]">{activeDonutTotal}</span>
              <span className="text-[10px] text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] uppercase tracking-wider">{config.donutCenterLabel}</span>
            </div>
          </div>

          <div className="space-y-2 pt-2 border-t border-[#22493c] dark:border-[#22493c] light:border-[#EBF2EE]">
            {activeDonutData.map((item) => (
              <div key={item.name} className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: item.color }}></span>
                  <span className="text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063]">{item.name}</span>
                </div>
                <span className="font-bold text-white dark:text-white light:text-[#0D1C17]">{item.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ========================================================================= */}
      {/* ROLE-SPECIFIC LIVE OPERATIONS FEED */}
      {/* ========================================================================= */}
      <div className="rounded-3xl p-6 sm:p-8 border transition-colors
        bg-[#152a23] dark:bg-[#152a23] border-[#22493c] dark:border-[#22493c]
        light:bg-white light:border-[#D5E4DC] light:shadow-sm
      ">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-bold text-white dark:text-white light:text-[#0D1C17]">
              {config.feedTitle}
            </h3>
            <p className="text-xs text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063]">
              {config.feedSub}
            </p>
          </div>
          <button
            onClick={() => onNavigate(config.feedNavTarget)}
            className="text-xs font-bold text-brand-400 hover:underline flex items-center"
          >
            {config.feedNavText} <ArrowUpRight className="w-3.5 h-3.5 ml-1" />
          </button>
        </div>

        <div className="divide-y divide-[#22493c] dark:divide-[#22493c] light:divide-[#EBF2EE]">
          {recentActivities.map((act) => (
            <div key={act.id} className="py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 first:pt-0 last:pb-0">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 rounded-full bg-brand-400 mt-1.5 shrink-0"></div>
                <div>
                  <h4 className="text-sm font-semibold text-white dark:text-white light:text-[#0D1C17]">
                    {act.title}
                  </h4>
                  <p className="text-xs text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] mt-0.5">
                    {act.desc}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3 sm:self-center shrink-0">
                <span className="text-xs text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] flex items-center">
                  <Clock className="w-3 h-3 mr-1" />
                  {act.time}
                </span>
                <StatusBadge status={act.status as any} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Interactive Analytic Breakdown Modal */}
      <AnalyticBreakdownModal
        isOpen={isBreakdownOpen}
        onClose={() => setIsBreakdownOpen(false)}
        metric={selectedMetric}
        timeframeLabel={currentData.label}
        datePreset={datePreset}
      />
    </div>
  );
};
